import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { async, BehaviorSubject, Observable } from 'rxjs';

import { AuthModalComponent } from '../components/auth-modal/auth-modal.component';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { PosthogService } from 'src/shared/services/posthog.service';
import { PmDemoLandingService } from 'src/shared/services/pm-demo-landing.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS, GUEST_LANDING_FLAG_KEYS } from 'src/shared/constants/feature-flags';
import { environment } from 'src/environments/environment';

export type AuthModalMode = 'auth' | 'convert' | 'guest-type';
export type AuthTab = 'signin' | 'signup';

@Injectable({
  providedIn: 'root'
})
export class AuthModalService {
  private isModalOpen = false;
  private isOpenSubject = new BehaviorSubject<boolean>(false);
  private isPendingGuestCreation = false;
  isOpen$: Observable<boolean> = this.isOpenSubject.asObservable();
  private tokenLoginLoadingSubject = new BehaviorSubject<boolean>(false);
  tokenLoginLoading$: Observable<boolean> = this.tokenLoginLoadingSubject.asObservable();

  constructor(
    private modalController: ModalController,
    private navCtrl: CustomNavController,
    private posthogService: PosthogService,
    private pmDemoLanding: PmDemoLandingService
  ) {}

  setTokenLoginLoading(loading: boolean): void {
    this.tokenLoginLoadingSubject.next(loading);
  }

  async openAuthModal(mode: AuthModalMode = 'auth', defaultTab: AuthTab = 'signup', alertMessage = '', signInOnly = false): Promise<boolean> {
    if (this.isModalOpen) {
      return false;
    }
    this.isModalOpen = true;
    this.isOpenSubject.next(true);
    const modal = await this.modalController.create({
      component: AuthModalComponent,
      componentProps: { initialMode: mode, defaultTab, alertMessage, signInOnly },
      cssClass: mode === 'guest-type' ? 'guest-type-modal-wrapper' : 'auth-modal-wrapper',
      backdropDismiss: mode !== 'guest-type',
      // The phone-login country dropdown (intl-tel-input) portals its search box to
      // document.body, outside the modal. Ionic's focus trap would otherwise block
      // keystrokes from reaching that search input, so users could only scroll.
      focusTrap: false
    });
    await modal.present();
    await modal.onDidDismiss();
    this.isModalOpen = false;
    this.isOpenSubject.next(false);
  }

  async openGuestTypeModal(): Promise<{ guestType: string } | null> {
    if (this.isModalOpen) {
      return null;
    }
    this.isModalOpen = true;
    this.isOpenSubject.next(true);
    const modal = await this.modalController.create({
      component: AuthModalComponent,
      componentProps: { initialMode: 'guest-type' },
      cssClass: 'guest-type-modal-wrapper',
      backdropDismiss: false,
      // Sign In inside this modal reaches the phone-login step, whose country
      // dropdown portals its search box to document.body. Ionic's focus trap
      // would block typing there, so disable it (same reason as openAuthModal).
      focusTrap: false
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    this.isModalOpen = false;
    this.isOpenSubject.next(false);
    if (data?.guestType) {
      const isDemoSession = localStorage.getItem('isDemoAccount') === 'true';
      if (isDemoSession) {
        // Property-manager demo landing page is A/B tested (pm-demo-landing-page).
        // Control = Strategy/Income overview; variants land on other pages.
        const route = await this.pmDemoLanding.resolveLandingRoute();
        this.navCtrl.navigateForward(route);
      } else {
        const variant = await this.resolveGuestLandingVariant();
        const route = variant === 'chat' ? 'chat' : 'properties';
        this.navCtrl.navigateForward(route);
      }
    }
    return data || null;
  }

  async dismissAuthModal(): Promise<void> {
    if (this.isModalOpen) {
      await this.modalController.dismiss();
      this.isModalOpen = false;
      this.isOpenSubject.next(false);
    }
  }

  hasSelectedGuestType(): boolean {
    return !!localStorage.getItem('guest_view_type');
  }

  getGuestViewType(): string | null {
    return localStorage.getItem('guest_view_type');
  }

  isOpen(): boolean {
    return this.isModalOpen;
  }

  startGuestCreation(): void {
    this.isPendingGuestCreation = true;
  }

  completeGuestCreation(): void {
    this.isPendingGuestCreation = false;
  }

  isGuestCreationPending(): boolean {
    return this.isPendingGuestCreation;
  }

  async resolveGuestLandingVariant(): Promise<string> {
    const flagKey = GUEST_LANDING_FLAG_KEYS[environment.environment] || GUEST_LANDING_FLAG_KEYS['production'];
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {return cached;}

    await this.posthogService.waitForFlags();
    const raw = await this.posthogService.getFeatureFlag(flagKey);
    const variant = (raw as string) || FEATURE_FLAG_DEFAULTS[FEATURE_FLAGS.GUEST_MODE_LANDING_PAGE] as string;

    if (raw !== null && raw !== undefined) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }
}
